import { legacy_createStore, applyMiddleware, AnyAction } from 'redux';
import logger from 'redux-logger';
import thunk from 'redux-thunk';
import { AxiosError } from 'axios';
import { localeEnglish } from 'shared-locales';
import api from 'api';
import { appsignalSendError } from 'lib/appsignal';
import { actions, createReducer } from '.';

import type { LocaleMessages, ServerConfig } from 'shared-types';
import type { AppDispatch, ReduxRootState } from 'types';

const isServer = typeof window === 'undefined';

const middlewares = [thunk];
if (process.env.NODE_ENV === 'development' && !isServer) {
  middlewares.push(logger);
}

const dispatchError =
  (reduxStore: ReturnType<typeof initStore>) =>
  async (error: Error | null, msg?: string) => {
    const { dispatch } = reduxStore;
    const { messages, user } = reduxStore.getState();

    dispatch.addNotice({ text: msg || messages.commonError, type: 'error' });

    if (process.env.NODE_ENV === 'production' && error) {
      try {
        appsignalSendError('HandleErrorDispatch', error, {
          user: { id: user?.id, roles: user?.roles }
        });

        const statusCode =
          error instanceof AxiosError
            ? error.response?.status || 'no status code'
            : 'not an axios error';

        await api.post('/admin/error', {
          error: {
            message: error.message,
            name: error.name,
            stack: error.stack
          },
          href: typeof window !== 'undefined' ? window.location.href : '',
          statusCode,
          userAgent:
            typeof window !== 'undefined' ? window.navigator.userAgent : ''
        });
      } catch {
        console.error('error in sending error info to api');
      }
    } else {
      console.warn('Dispatch captured error:', error);
    }
  };

const initStore = (
  messages: LocaleMessages,
  serverConfig: ServerConfig,
  headers: any,
  state: ReduxRootState
) => {
  const reducer = createReducer(
    messages || localeEnglish,
    serverConfig,
    headers,
    state
  );

  const reduxStore = legacy_createStore<
    ReduxRootState,
    AnyAction,
    { dispatch: AppDispatch },
    unknown
  >(reducer, state, applyMiddleware(...middlewares));

  // augment dispatch with defined actions as custom methods
  Object.keys(actions).forEach((key) => {
    reduxStore.dispatch[key] = (...args: any[]) => {
      return reduxStore.dispatch(actions[key](...args));
    };
  });

  // augment dispatch with a custom error method
  reduxStore.dispatch.handleErrorDispatch = dispatchError(reduxStore);

  return reduxStore;
};

export default initStore;
